import React, { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Box, FormControlLabel, Checkbox, IconButton, Tooltip as MuiTooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

const RotacionPersonal = ({ selectedIndustries = [], onBarClick = () => {} }) => {
  const [infoOpen, setInfoOpen] = useState(false);

  const allLabels = [
    'Manufactura',
    'Salud/Farmacéutica',
    'Retail',
    'Servicios logísticos',
    'Alimentos',
    'Construcción',
    'Automotriz',
  ];

  const deliveryData2023 = {
    'Manufactura': 8,
    'Salud/Farmacéutica': 10,
    'Retail': 23,
    'Servicios logísticos': 27,
    'Alimentos': 13,
    'Construcción': 11,
    'Automotriz': 14,
  };

  const deliveryData2024 = {
    'Manufactura': 48,
    'Salud/Farmacéutica': 30,
    'Retail': 28,
    'Servicios logísticos': 20,
    'Alimentos': 27,
    'Construcción': 12,
    'Automotriz': 25,
  };

  const colorMap = {
    '2022': '#4B73B1',
    '2023': '#A2AEBB',
  };

  const labels = selectedIndustries.length > 0 ? selectedIndustries : allLabels;
  const dataValues2023 = labels.map((label) => deliveryData2023[label]);
  const dataValues2024 = labels.map((label) => deliveryData2024[label]);

  const chartData = {
    labels: labels,
    datasets: [
      {
        label: '% Rotación (2022)',
        data: dataValues2023,
        backgroundColor: colorMap['2022'],
        borderColor: '#fff',
        borderWidth: 2,
      },
      {
        label: '% Rotación (2023)',
        data: dataValues2024,
        backgroundColor: colorMap['2023'],
        borderColor: '#fff',
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.raw}%`,
        },
      },
      datalabels: {
        color: '#000',
        font: { weight: 'bold', size: 14 },
        anchor: 'end',
        align: 'right',
        offset: -2,
        formatter: (value) => `${value}%`,
        clip: false,
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        max: 100,
        ticks: {
          stepSize: 10,
        },
        title: {
          display: true,
          text: 'Porcentaje (%)',
        },
      },
      y: {
        ticks: {
          font: { size: 14 },
        },
      },
    },
    onClick: (evt, elements) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        const industry = chartData.labels[index];
        handleSegmentClick(industry);
      }
    },
  };

  const handleSegmentClick = (industry) => {
    let updatedSelectedIndustries = [...selectedIndustries];
    if (updatedSelectedIndustries.includes(industry)) {
      updatedSelectedIndustries = updatedSelectedIndustries.filter((item) => item !== industry);
    } else {
      updatedSelectedIndustries.push(industry);
    }
    onBarClick(updatedSelectedIndustries);
  };

  const handleCheckboxChange = (industry) => {
    handleSegmentClick(industry);
  };

  const infoContent = {
    Analisis: 'En 2023, algunas industrias como Servicios logísticos mostraron una disminución en la rotación, mientras que otras experimentaron incrementos que podrían estar asociados a mayores exigencias operativas, cambios estructurales o condiciones del mercado laboral.',
    formula: 'Rotación Mensual (%) = (𝑁u𝑚𝑒𝑟𝑜 𝑑𝑒 𝑑𝑒𝑠𝑣𝑖𝑛𝑐𝑢𝑙𝑎𝑐𝑖𝑜𝑛𝑒𝑠 𝑑𝑒 á𝑟𝑒𝑎𝑠 𝑙𝑜𝑔í𝑠𝑡𝑖𝑐𝑎𝑠 𝑦 𝑑𝑒 𝑎𝑙𝑚𝑎𝑐é𝑛 𝑝𝑜𝑟 𝑚𝑒𝑠 / 𝑃𝑟𝑜𝑚𝑒𝑑𝑖𝑜 𝑑𝑒 𝐸𝑚𝑝𝑙𝑒𝑎𝑑𝑜𝑠 𝑑𝑒𝑙 𝑚𝑒𝑠) 𝑥 100 \n \n \n  Rotación anual: (Suma de rotaciones mensuales) / 12',
    description: 'La gráfica muestra el porcentaje de rotación de personal logístico por industria durante 2022 y 2023. Este indicador refleja la dinámica laboral dentro de las operaciones logísticas, siendo especialmente relevante para entender los retos en retención de talento, estabilidad operativa y continuidad en procesos críticos'  };
  

  return (
    <div style={{ maxWidth: '600px', height: '555px', margin: '0 auto' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MuiTooltip title="Información de la gráfica">
          <IconButton onClick={() => setInfoOpen(true)}>
            <InfoIcon />
          </IconButton>
        </MuiTooltip>
      </Box>
      <Box display="flex" justifyContent="center" flexWrap="wrap" mb={2}>
        {allLabels.map((industry) => (
          <FormControlLabel
            key={industry}
            control={
              <Checkbox
                checked={selectedIndustries.includes(industry)}
                onChange={() => handleCheckboxChange(industry)}
                color="primary"
              />
            }
            label={industry}
          />
        ))}
      </Box>
      <div style={{ width: '100%', height: '420px' }}>
        <Bar data={chartData} options={options} />
      </div>
  
      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)}>
        <DialogTitle>Información de la Gráfica</DialogTitle>
        <DialogContent>
        <p><strong>Análisis:</strong> {infoContent.Analisis}</p>
          <p><strong>Fórmula:</strong> {infoContent.formula}</p>
          <p><strong>Descripción:</strong> {infoContent.description}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
  
};

export default RotacionPersonal;
